import {
  buildResultList,
  loadAdvancedSearchQueryActions,
  ResultList,
  ResultList as HeadlessResultList,
  ResultListState,
  Pager,
} from '@coveo/headless';
import { VideoGalleryProps } from 'components/general/VideoGallery/VideoGallery';
import { CoveoEngineContext, getFieldsToInclude } from 'lib/coveo';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import {
  defaultVideoFieldsToInclude,
  getVideoItemListProps,
} from 'src/helpers/VideoGalleryHelpers/VideoItemUtils.Helper';
import VideoListing from 'src/helpers/VideoGalleryHelpers/VideoListing';

export type GlobalVideoGalleryProps = {
  controller: ResultList;
  pagerController?: Pager;
  videoResultItems: Feature.EnterpriseWeb.Enterprise.Elements.Search.VideoResultItem[];
  hasFacets: boolean;
  queryPipeline?: string;
  searchHub?: string;
  language?: string;
  farmName?: string;
};

export const GlobalVideoGallery = ({
  controller,
  videoResultItems,
  pagerController,
  language = 'en',
  farmName,
}: GlobalVideoGalleryProps) => {
  const [videoListState, setVideoListState] = useState<ResultListState | undefined>(
    controller?.state
  );

  const engine = useContext(CoveoEngineContext);
  const videoDeepLinkResultList = useRef<HeadlessResultList>();

  useEffect(() => {
    const unsubscribe = controller?.subscribe(() => setVideoListState(controller.state));

    return unsubscribe;
  }, [controller]);

  useEffect(() => {
    if (!engine || videoDeepLinkResultList.current) {
      return;
    }

    videoDeepLinkResultList.current = buildResultList(engine, {
      options: {
        fieldsToInclude: [
          ...getFieldsToInclude(videoResultItems, 'video'),
          'sc_templateid',
          ...defaultVideoFieldsToInclude,
        ],
      },
    });
  }, [engine, videoResultItems]);

  const resolveDeepLinkedVideo = async (requestedVideoValue: string) => {
    if (!engine || !videoDeepLinkResultList.current || !farmName) {
      return undefined;
    }

    const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

    engine.dispatch(
      updateAdvancedSearchQueries({
        cq: `(@ew_excludefromsearch=="false") AND (@ew_sitelanguage==${language}) AND (@source==EnterpriseWeb-${farmName}) AND (@ew_videoanchorid=="${requestedVideoValue}")`,
        aq: '',
      })
    );

    await new Promise<void>((resolve) => {
      const unsubscribe = videoDeepLinkResultList.current?.subscribe(() => {
        const state = videoDeepLinkResultList.current?.state;

        if (state && !state.isLoading) {
          unsubscribe?.();
          resolve();
        }
      });

      engine.executeFirstSearch();
    });

    const state = videoDeepLinkResultList.current.state;
    const firstResult = state.results?.[0];

    if (!firstResult) {
      return undefined;
    }

    const items = getVideoItemListProps(
      {
        ...state,
        results: [firstResult],
      } as ResultListState,
      videoResultItems
    );

    return items?.[0];
  };

  const getVideoListingProps = () => {
    return {
      fields: {
        videos: videoListState && getVideoItemListProps(videoListState, videoResultItems),
        componentSpacing: null,
        sectionId: {
          value: '',
        },
        eventName: {
          value: '',
        },
        eventType: {
          value: '',
        },
        eventZone: {
          value: '',
        },
      },
    };
  };
  return (
    <div className="grid gap-s md:grid-cols-12 md:gap-s" id="globalVideoGallery">
      <VideoListing
        isCoveoDrivenPagination
        pagerController={pagerController}
        resolveDeepLinkedVideo={resolveDeepLinkedVideo}
        {...(getVideoListingProps() as unknown as VideoGalleryProps)}
      />
    </div>
  );
};
